/**
 * Network-tab extractors (Experion EMDB enterprise / PCN). Pure
 * functions, no RTK / no Neo4j driver — mirror `extract/dcs.ts`.
 */

import { extractNode, extractNodeList, extractRawNode, toIntCount } from '../graph-row'
import { extractComputeRefs } from '../refs'
import { compareByNameThenId } from '../sort'
import { extractPlantMeta } from './provenance'
import type {
  CypherReadResult,
  PlantAsset,
  PlantNetworkDevice,
  PlantNetworkNodeDetail,
  PlantNetworkNodeKind,
  PlantNetworkParentRef,
  PlantNetworkProjectRef,
  PlantNetworkSegment,
  PlantNetworkSiteRef,
  PlantNetworkTree,
  PlantNetworkTreeNode
} from '../types'

const TREE_KINDS: ReadonlySet<PlantNetworkNodeKind> = new Set([
  'Asset',
  'NetworkSegment',
  'NetworkDevice'
])

export function mapPlantNetworkTree(raw: CypherReadResult): PlantNetworkTree {
  const nodes: PlantNetworkTreeNode[] = []
  const seen = new Set<string>()
  for (const row of raw.results) {
    const node = extractRawNode(row['node'])
    if (!node) continue
    const kind = row['kind']
    if (typeof kind !== 'string' || !TREE_KINDS.has(kind as PlantNetworkNodeKind)) continue
    const id = typeof node['id'] === 'string' ? node['id'] : (node['_id'] as string)
    if (!id || seen.has(id)) continue
    seen.add(id)
    const name = typeof node['name'] === 'string' ? node['name'] : id
    const displayName =
      typeof node['display_name'] === 'string' && node['display_name'].trim()
        ? node['display_name'].trim()
        : undefined
    const parentId = typeof row['parentId'] === 'string' ? row['parentId'] : null
    const projectId = typeof row['projectId'] === 'string' ? row['projectId'] : null
    const projectName = typeof row['projectName'] === 'string' ? row['projectName'] : null
    nodes.push({
      id,
      parentId,
      kind: kind as PlantNetworkNodeKind,
      name,
      ...(displayName ? { display_name: displayName } : {}),
      ...extractPlantMeta(node),
      childCount: toIntCount(row['childCount']),
      projectId,
      projectName
    })
  }
  return { nodes }
}

function isStrayFor(row: Record<string, unknown>, kind: string): boolean {
  const strayLabels = Array.isArray(row['strayLabels'])
    ? row['strayLabels'].filter((label): label is string => typeof label === 'string')
    : []
  return strayLabels.includes(kind)
}

function extractParentRef(value: unknown, kindValue: unknown): PlantNetworkParentRef | null {
  const node = extractNode<PlantAsset | PlantNetworkSegment | PlantNetworkDevice>(value)
  if (!node) return null
  if (kindValue !== 'Asset' && kindValue !== 'NetworkSegment' && kindValue !== 'NetworkDevice') {
    return null
  }
  return { id: node.id, name: node.display_name?.trim() || node.name, kind: kindValue }
}

function extractSiteRef(value: unknown): PlantNetworkSiteRef | null {
  const node = extractNode<{ id: string; _id: string; name: string }>(value)
  if (!node) return null
  return { id: node.id, name: node.name }
}

function extractProjectRef(value: unknown): PlantNetworkProjectRef | null {
  const node = extractNode<{ id: string; _id: string; name: string }>(value)
  if (!node) return null
  return { id: node.id, name: node.name }
}

export function mapPlantNetworkNodeDetail(
  raw: CypherReadResult,
  nodeId: string
): PlantNetworkNodeDetail {
  const row = raw.results[0]
  if (!row) {
    throw new Error(`Network node ${nodeId} not found`)
  }
  const labels = Array.isArray(row['labelList']) ? (row['labelList'] as unknown[]) : []

  const parent = extractParentRef(row['treeParent'], row['treeParentKind'])
  const siteParent = extractSiteRef(row['siteParent'])
  const project = extractProjectRef(row['project'])
  const computes = extractComputeRefs(row['computes'])

  if (labels.includes('Asset')) {
    const asset = extractNode<PlantAsset>(row['n'])
    if (!asset) throw new Error(`Asset ${nodeId} could not be projected`)
    return {
      kind: 'Asset',
      isStray: isStrayFor(row, 'Asset'),
      asset,
      parent,
      siteParent,
      project,
      childAssets: extractNodeList<PlantAsset>(row['childAssets']).sort(compareByNameThenId),
      segments: extractNodeList<PlantNetworkSegment>(row['childSegments']).sort(
        compareByNameThenId
      ),
      devices: extractNodeList<PlantNetworkDevice>(row['childDevices']).sort(compareByNameThenId),
      computes
    }
  }
  if (labels.includes('NetworkSegment')) {
    const segment = extractNode<PlantNetworkSegment>(row['n'])
    if (!segment) throw new Error(`NetworkSegment ${nodeId} could not be projected`)
    return {
      kind: 'NetworkSegment',
      isStray: isStrayFor(row, 'NetworkSegment'),
      segment,
      parent,
      siteParent,
      project,
      devices: extractNodeList<PlantNetworkDevice>(row['childDevices']).sort(compareByNameThenId),
      computes
    }
  }
  if (labels.includes('NetworkDevice')) {
    const device = extractNode<PlantNetworkDevice>(row['n'])
    if (!device) throw new Error(`NetworkDevice ${nodeId} could not be projected`)
    return {
      kind: 'NetworkDevice',
      isStray: isStrayFor(row, 'NetworkDevice'),
      device,
      parent,
      siteParent,
      project,
      devices: extractNodeList<PlantNetworkDevice>(row['childDevices']).sort(compareByNameThenId),
      computes
    }
  }
  throw new Error(`Network node ${nodeId} has unsupported labels ${labels.join(',')}`)
}
